import { endOfYear, isSameDay, isValid, parse, startOfYear, subDays, subYears } from 'date-fns';

import { formatDate } from '@/utils';

const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

// Fixed start floor for the "All Time" preset (local midnight). Matches the reporting
// window's practical lower bound; a household-inception source can replace this later.
export const ALL_TIME_FLOOR = new Date(2010, 0, 1);

// Parses the yyyy-MM-dd calendar portion of a query-param value into a LOCAL-midnight Date,
// ignoring any trailing zone designator (formatDate('SERVER') emits e.g. "2026-01-01Z" carrying
// the local Y/M/D). This mirrors how presets/calendar produce dates (local midnight); using the
// utils' parseLocalDate here would read the string as UTC and shift a day for negative-offset
// (US) users. Returns null for a missing, malformed, or out-of-range value.
const parseCalendarDate = (value: string): Date | null => {
    const match = CALENDAR_DATE.exec(value);
    if (!match) {
        return null;
    }
    const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
    const date = new Date(year, month - 1, day);
    // Reject rolled-over values from a malformed param (e.g. month 13 → next year).
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        return null;
    }
    return date;
};

export interface DateRange {
    fromInclusive: Date;
    toInclusive: Date;
}

export type RangePresetId = 'ALL_TIME' | 'LAST_7' | 'LAST_30' | 'LAST_90' | 'ONE_YEAR' | 'LAST_YEAR' | 'YTD' | 'CUSTOM';

// Fixed presets computed relative to `today`. CUSTOM is driven by the calendar, not this helper.
export const presetRange = (id: RangePresetId, today: Date = new Date()): DateRange => {
    switch (id) {
        case 'ALL_TIME':
            return { fromInclusive: ALL_TIME_FLOOR, toInclusive: today };
        case 'LAST_7':
            return { fromInclusive: subDays(today, 7), toInclusive: today };
        case 'LAST_30':
            return { fromInclusive: subDays(today, 30), toInclusive: today };
        case 'LAST_90':
            return { fromInclusive: subDays(today, 90), toInclusive: today };
        case 'ONE_YEAR':
            return { fromInclusive: subYears(today, 1), toInclusive: today };
        case 'LAST_YEAR':
            return {
                fromInclusive: startOfYear(subYears(today, 1)),
                toInclusive: endOfYear(subYears(today, 1)),
            };
        case 'YTD':
        case 'CUSTOM':
        default:
            return { fromInclusive: startOfYear(today), toInclusive: today };
    }
};

// The fixed presets, in the order shown in the picker rail, paired with human-readable labels.
// Mirrors client-portal's `dateUnit` set (QTD intentionally omitted to match).
export const RANGE_PRESETS: { id: Exclude<RangePresetId, 'CUSTOM'>; label: string }[] = [
    { id: 'ALL_TIME', label: 'All Time' },
    { id: 'LAST_7', label: 'Last 7 days' },
    { id: 'LAST_30', label: 'Last 30 days' },
    { id: 'LAST_90', label: 'Last 90 days' },
    { id: 'ONE_YEAR', label: 'Last 12 months' },
    { id: 'LAST_YEAR', label: 'Last Year' },
    { id: 'YTD', label: 'Year to Date' },
];

// Which preset (if any) a range corresponds to, so the picker can highlight it. A hand-picked
// range that matches no preset is 'CUSTOM'.
export const matchPreset = (range: DateRange, today: Date = new Date()): RangePresetId => {
    const match = RANGE_PRESETS.find(({ id }) => {
        const preset = presetRange(id, today);
        return isSameDay(preset.fromInclusive, range.fromInclusive) && isSameDay(preset.toInclusive, range.toInclusive);
    });
    return match?.id ?? 'CUSTOM';
};

// Parses a start/end pair of yyyy-MM-dd query-param values into a range. Returns null when
// either is absent, unparseable, or the two are out of order — callers then fall back to the
// default range, so a hand-edited URL can never crash the view.
export const parseRange = (start: string | null, end: string | null): DateRange | null => {
    if (!start || !end) {
        return null;
    }
    const fromInclusive = parseCalendarDate(start);
    const toInclusive = parseCalendarDate(end);
    if (!fromInclusive || !toInclusive || fromInclusive > toInclusive) {
        return null;
    }
    return { fromInclusive, toInclusive };
};

// Trigger label, e.g. "Jan 1, 2026 – Jul 22, 2026" (en dash), using the HUMAN date format.
export const formatRangeLabel = ({ fromInclusive, toInclusive }: DateRange): string =>
    `${formatDate(fromInclusive, 'HUMAN')} – ${formatDate(toInclusive, 'HUMAN')}`;

// Canonical display string for the manual date inputs (e.g. "Jul 22, 2026"). Matches the
// trigger label so a committed value re-renders in the same shape the user can type back.
export const formatManualDate = (date: Date): string => formatDate(date, 'HUMAN');

// Human-typed date formats accepted by the manual inputs, tried in order after the ISO form.
const MANUAL_DATE_FORMATS = ['MMM d, yyyy', 'MMMM d, yyyy', 'M/d/yyyy'];

// Parses a hand-typed date into a LOCAL-midnight Date. Accepts the ISO calendar form
// ("2026-07-22", tolerating a trailing "Z") plus the human/slash forms in MANUAL_DATE_FORMATS
// (e.g. "Jul 22, 2026", "7/22/2026"). Returns null for an empty or unrecognized value.
export const parseManualDate = (text: string): Date | null => {
    const trimmed = text.trim();
    if (!trimmed) {
        return null;
    }
    const iso = parseCalendarDate(trimmed);
    if (iso) {
        return iso;
    }
    for (const dateFormat of MANUAL_DATE_FORMATS) {
        const parsed = parse(trimmed, dateFormat, new Date());
        if (isValid(parsed)) {
            // Drop any time component so it matches how presets/calendar produce dates.
            return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
        }
    }
    return null;
};

// Computes the range produced by manually editing one edge to `raw` (a typed date string),
// keeping the other edge. Returns null when `raw` is empty/unparseable or the edit would put the
// edges out of order, so the caller can leave the current range untouched (the input reverts).
export const applyManualEdit = (current: DateRange, edge: 'from' | 'to', raw: string): DateRange | null => {
    const parsed = parseManualDate(raw);
    if (!parsed) {
        return null;
    }
    const next: DateRange =
        edge === 'from'
            ? { fromInclusive: parsed, toInclusive: current.toInclusive }
            : { fromInclusive: current.fromInclusive, toInclusive: parsed };
    return next.fromInclusive <= next.toInclusive ? next : null;
};
